import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { MoneyAccount } from 'src/features/moneyAccounts/utils/types';
import { useDeleteMoneyAccount } from 'src/features/moneyAccounts/api/useDeleteMoneyAccount';
import { LIST_MONEY_ACCOUNTS_RESOURCE } from 'src/features/moneyAccounts/api/useListMoneyAccounts';
import { moneyAccountsMessages } from 'src/features/moneyAccounts/utils/messages';
import { useOnError } from 'src/utils/errors';

interface DeleteMoneyAccountFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMoneyAccount: MoneyAccount;
    children?: React.ReactNode;
}

function DeleteBillingEntityFormModal<TData>({
    selectedMoneyAccount,
    children,
}: DeleteMoneyAccountFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const { mutate: deletePayStation } = useDeleteMoneyAccount({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MONEY_ACCOUNTS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(moneyAccountsMessages.deleteMoneyAccountSuccess),
                description: formatMessage(moneyAccountsMessages.deleteMoneyAccountSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...moneyAccountsMessages.deleteMoneyAccount },
                subTitle: {
                    ...moneyAccountsMessages.deleteMoneyAccountSubtitle,
                    values: {
                        entity: selectedMoneyAccount.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...moneyAccountsMessages.confirmDeleteMoneyAccount },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: moneyAccountsMessages.deleteMoneyAccount,
                callbackFn: () => {
                    return (
                        deletePayStation({
                            id: selectedMoneyAccount.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteBillingEntityFormModal;
